'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  Shield,
  Check,
  ChevronDown,
  ChevronUp,
  X,
  Monitor,
  BookOpen,
  Mic,
  Brain,
  GraduationCap,
  FileText,
  BarChart3,
  Users,
  Globe,
  Zap,
  Clock,
  Star,
  Award,
  Heart,
  Target,
  Calendar,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import RalphCheckoutForm from './ralph-checkout-form'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: 'easeOut' },
  }),
}

// ============================================================
// ENTREGAVEIS DETALHADOS
// ============================================================

const entregaveis = [
  {
    categoria: 'Plataforma e Posicionamento Digital',
    icon: Monitor,
    cor: 'teal',
    items: [
      { nome: 'Sistema web com área de membros', desc: 'Plataforma com login/senha, gestão de usuários e ambiente seguro para seus alunos acessarem cursos e conteúdos' },
      { nome: 'Landing pages ilimitadas', desc: 'Páginas de captura, vendas, webinário e obrigado com design profissional otimizado para conversão' },
      { nome: 'Integrações diversas', desc: 'Pagamento (Assas), email marketing, analytics, webhooks e integração com sua plataforma de diagnóstico NR1' },
      { nome: 'Blog profissional integrado', desc: 'Sistema de blog SEO integrado à plataforma para publicação dos 60 artigos e posicionamento no Google' },
    ],
  },
  {
    categoria: 'Inteligência Artificial',
    icon: Brain,
    cor: 'purple',
    items: [
      { nome: 'Instalação e treinamento de Claude Code', desc: 'Setup completo da IA no seu computador com treinamento hands-on para você usar no dia a dia' },
      { nome: 'Segundo cérebro no Obsidian', desc: 'Vault estruturado com seus conhecimentos, metodologias e conteúdos organizados para consulta e produção' },
      { nome: 'Passaporte de contexto NR1', desc: 'Documento completo que mapeia seu conhecimento sobre Risco Psicossocial e Saúde Mental nas empresas' },
    ],
  },
  {
    categoria: 'Curso Completo',
    icon: GraduationCap,
    cor: 'blue',
    items: [
      { nome: 'Cronograma completo do curso', desc: 'Estrutura modular com sequência pedagógica, carga horária e objetivos de aprendizagem por aula' },
      { nome: 'Roteiros em 4 formatos', desc: 'Cada aula produzida em slide (apresentação), HTML (visual), áudio (narração TTS) e teleprompter (gravação)' },
      { nome: 'Conteúdo baseado na NR1', desc: 'Todo o curso estruturado sobre Risco Psicossocial e Saúde Mental no trabalho, alinhado com a legislação' },
    ],
  },
  {
    categoria: 'Livro Autoral',
    icon: BookOpen,
    cor: 'amber',
    items: [
      { nome: 'Criação do livro completo', desc: 'Estruturação, escrita assistida por IA com sua voz autoral, revisão gramatical e editorial' },
      { nome: 'Diagramação profissional', desc: 'Layout 14x21cm para gráfica com capa, sumário, ficha catalográfica e folha de rosto' },
      { nome: 'Impressão de 2 cópias', desc: 'Duas unidades físicas impressas em gráfica profissional para você ter em mãos' },
    ],
  },
  {
    categoria: 'Funil e Vendas',
    icon: Target,
    cor: 'rose',
    items: [
      { nome: 'Funil evergreen completo', desc: 'Estrutura de webinário automatizado que vende 24h por dia sem você precisar estar ao vivo' },
      { nome: 'Escopo de palestra presencial', desc: 'Roteiro estruturado para palestra de 60-90min sobre NR1, pronto para eventos corporativos' },
      { nome: 'Programa de mentoria', desc: 'Estrutura completa baseada no curso e livro, com cronograma de encontros e materiais de apoio' },
    ],
  },
  {
    categoria: 'Marketing de Conteúdo',
    icon: BarChart3,
    cor: 'emerald',
    items: [
      { nome: 'Blog com 60 artigos', desc: 'Conteúdo SEO otimizado sobre NR1, saúde mental e risco psicossocial para ranquear no Google' },
      { nome: 'Podcast com resumo do livro/curso', desc: 'Episódios em áudio profissional que condensam os principais conceitos para alcançar nova audiência' },
    ],
  },
]

// ============================================================
// CRONOGRAMA
// ============================================================

const cronograma = [
  {
    semana: 'Semanas 1-2',
    fase: 'Descoberta e Estruturação',
    items: ['Passaporte de contexto NR1', 'Instalação Claude Code + Obsidian', 'Cronograma do curso', 'Estrutura do livro'],
    cor: 'teal',
  },
  {
    semana: 'Semanas 3-6',
    fase: 'Produção de Conteúdo',
    items: ['Roteiros do curso (4 formatos)', 'Escrita do livro capítulo a capítulo', 'Roteiro da palestra', 'Primeiros artigos do blog'],
    cor: 'blue',
  },
  {
    semana: 'Semanas 7-9',
    fase: 'Plataforma e Revisão',
    items: ['Deploy da plataforma com sua marca', 'Área de membros + landing pages', 'Revisão e diagramação do livro', 'Integração de pagamentos'],
    cor: 'purple',
  },
  {
    semana: 'Semanas 10-12',
    fase: 'Publicação e Lançamento',
    items: ['Livro impresso (2 cópias)', 'Blog completo (60 artigos)', 'Podcast publicado', 'Funil evergreen ativo', 'Programa de mentoria estruturado'],
    cor: 'amber',
  },
]

// ============================================================
// COMPARATIVO DE VALOR
// ============================================================

const comparativo = [
  { item: 'Plataforma web personalizada', valorMercado: 'R$ 8.000 - R$ 15.000' },
  { item: 'Curso com roteiros profissionais', valorMercado: 'R$ 3.000 - R$ 6.000' },
  { item: 'Livro autoral + diagramação + impressão', valorMercado: 'R$ 4.000 - R$ 8.000' },
  { item: 'Blog com 60 artigos SEO', valorMercado: 'R$ 6.000 - R$ 12.000' },
  { item: 'Funil evergreen + webinário', valorMercado: 'R$ 3.000 - R$ 5.000' },
  { item: 'Podcast profissional', valorMercado: 'R$ 2.000 - R$ 4.000' },
  { item: 'Setup IA + segundo cérebro', valorMercado: 'R$ 2.000 - R$ 3.000' },
  { item: 'Palestra + programa de mentoria', valorMercado: 'R$ 2.000 - R$ 4.000' },
]

// ============================================================
// PLANOS DE PAGAMENTO
// ============================================================

const planos = [
  {
    id: 'nr1-entrada',
    nome: 'Entrada via PIX',
    subtitulo: 'Primeira parcela do projeto',
    preco: 10000,
    precoCentavos: 1000000,
    parcelas: 1,
    parcelaValor: '10.000',
    cor: 'teal',
    destaque: true,
    desconto: false,
    icon: Zap,
    badge: 'Sinal para iniciar',
  },
]

const ASSINATURA_SEMANAL_URL = 'https://www.asaas.com/i/3g3qbv3c7khilg45'

// ============================================================
// FAQ
// ============================================================

const faq = [
  {
    pergunta: 'Em quanto tempo o projeto fica pronto?',
    resposta: 'O cronograma previsto é de 12 semanas (3 meses). Começamos pela estruturação do conteúdo e terminamos com tudo publicado e funcionando. Você acompanha cada etapa e aprova antes de avançar.',
  },
  {
    pergunta: 'Preciso ter conhecimento técnico?',
    resposta: 'Não. Toda a parte técnica (plataforma, integrações, deploy) fica por nossa conta. O que precisamos de você é o conhecimento sobre NR1 e saúde mental nas empresas. O Passaporte de Contexto captura tudo isso de forma estruturada.',
  },
  {
    pergunta: 'O sistema é meu? Tenho controle total?',
    resposta: 'Sim. A plataforma fica no seu domínio, com seu login admin, sua marca. Você tem controle total sobre usuários, conteúdo e configurações. Não é um aluguel, é um ativo seu.',
  },
  {
    pergunta: 'E se eu quiser mudar algo depois?',
    resposta: 'A plataforma é construída com código aberto (Next.js, React, Supabase). Qualquer desenvolvedor pode dar manutenção. Além disso, com o Claude Code instalado, você mesmo pode fazer ajustes guiados pela IA.',
  },
  {
    pergunta: 'Os 60 artigos são genéricos?',
    resposta: 'Não. Cada artigo é produzido com base no seu Passaporte de Contexto, otimizado para SEO e escrito com técnicas anti-IA para garantir autenticidade. São artigos que ranqueiam no Google e geram tráfego orgânico.',
  },
  {
    pergunta: 'Como funciona o pagamento?',
    resposta: 'O investimento total é de R$ 20.000. Você paga uma entrada de R$ 10.000 (PIX ou cartão em até 10x) e o restante é dividido em 5 parcelas semanais de R$ 2.000 via link de pagamento.',
  },
  {
    pergunta: 'Como funcionam as parcelas semanais?',
    resposta: 'Após a entrada de R$ 10.000 (PIX ou boleto), você ativa a assinatura semanal de R$ 2.000 via link seguro do Assas. São 5 cobranças automáticas, uma por semana. Você recebe recibo por email a cada cobrança.',
  },
  {
    pergunta: 'E se eu não gostar do resultado?',
    resposta: 'Cada entrega é aprovada por você antes de avançar. Não existe surpresa no final. Se em algum ponto o resultado não te atender, ajustamos até ficar do jeito que você precisa.',
  },
  {
    pergunta: 'Vocês já fizeram algo parecido?',
    resposta: 'Sim. A plataforma é baseada no mesmo sistema que roda o Negócios S/A (app.antoniofogaca.com.br) - com área de membros, curso com 31 aulas, blog e integrações. Também produzimos 6 livros com diagramação profissional e mais de 140 aulas de cursos publicados.',
  },
  {
    pergunta: 'O que é o Passaporte de Contexto?',
    resposta: 'É um documento que mapeia todo o seu conhecimento especializado sobre NR1 usando a Metodologia APE (Arquitetura de Produtos Educacionais). Ele serve como base para TODOS os conteúdos: curso, livro, artigos, palestra. Garante consistência e profundidade em tudo que é produzido.',
  },
]

// ============================================================
// COMPONENTE
// ============================================================

export default function RalphPropostaPage() {
  const [selectedPlan, setSelectedPlan] = useState<typeof planos[0] | null>(null)
  const [showCheckout, setShowCheckout] = useState(false)
  const [faqAberto, setFaqAberto] = useState<number | null>(null)
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  function handleSelectPlan(plano: typeof planos[0]) {
    setSelectedPlan(plano)
    setShowCheckout(true)
    setTimeout(() => {
      document.getElementById('checkout-section')?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const corClasses: Record<string, { bg: string; border: string; text: string; badge: string; btn: string; btnHover: string; light: string }> = {
    teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', badge: 'bg-teal-100 text-teal-700', btn: 'bg-teal-600', btnHover: 'hover:bg-teal-700', light: 'bg-teal-50' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', btn: 'bg-blue-600', btnHover: 'hover:bg-blue-700', light: 'bg-blue-50' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', btn: 'bg-purple-600', btnHover: 'hover:bg-purple-700', light: 'bg-purple-50' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', btn: 'bg-amber-600', btnHover: 'hover:bg-amber-700', light: 'bg-amber-50' },
    rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700', btn: 'bg-rose-600', btnHover: 'hover:bg-rose-700', light: 'bg-rose-50' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', btn: 'bg-emerald-600', btnHover: 'hover:bg-emerald-700', light: 'bg-emerald-50' },
  }

  const totalMercado = 'R$ 30.000 a R$ 57.000'

  return (
    <main className="min-h-screen bg-[#f8fafb]">
      {/* Header */}
      <header className="w-full py-4 px-6 border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-5xl flex items-center gap-3">
          <Image
            src="/images/jonathan-machado.jpg"
            alt="Jonathan Machado"
            width={40}
            height={40}
            className="rounded-full"
          />
          <div className="flex flex-col">
            <span className="text-lg font-semibold text-gray-900 leading-tight">Jonathan Machado</span>
            <span className="text-xs text-gray-400">Proposta Comercial - Grupo R4</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-4 pt-10 pb-6 md:pt-16 text-center">
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-100 px-4 py-1.5 text-sm font-medium text-teal-700 mb-6">
            <Shield className="h-3.5 w-3.5" />
            Proposta exclusiva - Dr. Ralph Caires Rudolph
          </span>
        </motion.div>

        <motion.h1
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          Sua esteira de produtos completa
          <br />
          <span className="text-teal-600">para dominar o mercado de NR1</span>
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-6"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          Curso, livro, blog, podcast, funil evergreen e programa de mentoria.
          Tudo construído sobre o seu conhecimento em Saúde Mental e Risco Psicossocial nas empresas.
        </motion.p>

        <motion.div
          className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-gray-500"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          <span className="flex items-center gap-1.5">
            <Monitor className="h-4 w-4 text-teal-500" />
            Plataforma própria
          </span>
          <span className="flex items-center gap-1.5">
            <BookOpen className="h-4 w-4 text-amber-500" />
            Livro impresso
          </span>
          <span className="flex items-center gap-1.5">
            <GraduationCap className="h-4 w-4 text-blue-500" />
            Curso completo
          </span>
          <span className="flex items-center gap-1.5">
            <Globe className="h-4 w-4 text-emerald-500" />
            60 artigos SEO
          </span>
          <span className="flex items-center gap-1.5">
            <Mic className="h-4 w-4 text-purple-500" />
            Podcast
          </span>
        </motion.div>
      </section>

      {/* Contexto do Projeto */}
      <section className="mx-auto max-w-4xl px-4 pb-8">
        <motion.div
          className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">Seu cenário atual</h3>
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p>
              Você é <span className="font-semibold text-gray-900">médico psiquiatra</span> com
              expertise em saúde mental corporativa. Já construiu uma{' '}
              <span className="font-semibold text-gray-900">plataforma SaaS de diagnóstico NR1</span>{' '}
              que permite empresas mapearem riscos psicossociais, classificarem por cores
              (verde, amarelo, laranja, vermelho) e receberem trilhas educacionais direcionadas ao
              nível de adoecimento detectado.
            </p>
            <p>
              A plataforma de diagnóstico já existe e está em operação com o Tiago (dev).
              O que falta é a <span className="font-semibold text-teal-700">esteira de produtos educacionais</span>{' '}
              que vai posicionar você como referência no mercado, atrair empresas para a plataforma
              e criar múltiplos pontos de entrada no seu ecossistema.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                <p className="font-semibold text-teal-800 text-xs uppercase tracking-wide mb-1">Propósito</p>
                <p className="text-teal-700">Melhores pessoas, melhores empresas, melhores resultados. Transformação cultural através da saúde mental.</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="font-semibold text-blue-800 text-xs uppercase tracking-wide mb-1">Modelo de negócio</p>
                <p className="text-blue-700">LTV por recorrência. Empresa que contrata hoje renova ano após ano. Créditos de diagnóstico + trilhas educacionais.</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <p className="font-semibold text-amber-800 text-xs uppercase tracking-wide mb-1">Diferencial</p>
                <p className="text-amber-700">Solução tecnológica com capilaridade e velocidade. Preço competitivo por escala. Diagnóstico + tratamento integrados.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Urgencia NR1 */}
      <section className="mx-auto max-w-4xl px-4 pb-12">
        <motion.div
          className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6 md:p-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <div className="flex items-start gap-4">
            <div className="bg-amber-100 rounded-xl p-3 shrink-0">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                A NR1 já é obrigatória e as empresas estão correndo atrás
              </h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Todas as empresas brasileiras precisam mapear e gerenciar riscos psicossociais no ambiente de trabalho.
                Você já tem a plataforma de diagnóstico. Agora precisa da esteira completa para escalar: curso, livro, blog, podcast e funil.
                Quem se posiciona agora como referência captura o mercado antes da concorrência.
              </p>
              <p className="text-sm text-amber-700 font-medium">
                Você tem o conhecimento e a ferramenta. Falta a esteira de produtos para escalar.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Entregaveis */}
      <section className="mx-auto max-w-5xl px-4 pb-16" id="entregaveis">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          O que está incluso no projeto
        </motion.h2>
        <motion.p
          className="text-center text-gray-500 mb-10 max-w-2xl mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Cada item abaixo é um entregável concreto. Sem surpresas, sem letras miúdas.
        </motion.p>

        <div className="space-y-6">
          {entregaveis.map((grupo, gi) => {
            const GrupoIcon = grupo.icon
            const cores = corClasses[grupo.cor]
            return (
              <motion.div
                key={gi}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={gi * 0.5}
              >
                <div className={`${cores.light} px-6 py-4 border-b border-gray-100 flex items-center gap-3`}>
                  <div className={`${cores.bg} p-2 rounded-lg`}>
                    <GrupoIcon className={`h-5 w-5 ${cores.text}`} />
                  </div>
                  <h3 className="font-bold text-gray-900">{grupo.categoria}</h3>
                  <span className={`ml-auto text-xs font-medium ${cores.badge} px-2.5 py-1 rounded-full`}>
                    {grupo.items.length} {grupo.items.length === 1 ? 'entregável' : 'entregáveis'}
                  </span>
                </div>
                <div className="divide-y divide-gray-50">
                  {grupo.items.map((item, ii) => (
                    <div key={ii} className="px-6 py-4 flex items-start gap-3">
                      <CheckCircle2 className={`h-5 w-5 ${cores.text} shrink-0 mt-0.5`} />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{item.nome}</p>
                        <p className="text-sm text-gray-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* Cronograma */}
      <section className="bg-white py-16 border-y border-gray-100">
        <div className="mx-auto max-w-4xl px-4">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            Cronograma de implantação
          </motion.h2>
          <motion.p
            className="text-center text-gray-500 mb-10"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            12 semanas do início à publicação completa
          </motion.p>

          <div className="relative">
            {/* Linha vertical */}
            <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-gray-200" />

            <div className="space-y-8">
              {cronograma.map((fase, i) => {
                const cores = corClasses[fase.cor]
                return (
                  <motion.div
                    key={i}
                    className="relative pl-16 md:pl-20"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={i}
                  >
                    {/* Bolinha */}
                    <div className={`absolute left-3.5 md:left-5.5 w-5 h-5 rounded-full ${cores.btn} border-4 border-white shadow-md`} />

                    <div className={`${cores.light} rounded-xl p-5 border ${cores.border}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`text-xs font-bold ${cores.badge} px-2.5 py-1 rounded-full`}>{fase.semana}</span>
                        <h4 className="font-bold text-gray-900">{fase.fase}</h4>
                      </div>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {fase.items.map((item, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                            <Check className={`h-4 w-4 ${cores.text} shrink-0`} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Comparativo de Valor */}
      <section className="mx-auto max-w-4xl px-4 py-16">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          O valor real desse projeto
        </motion.h2>
        <motion.p
          className="text-center text-gray-500 mb-10 max-w-2xl mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Se você contratasse cada serviço separadamente no mercado, gastaria entre {totalMercado}.
        </motion.p>

        <motion.div
          className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          <div className="divide-y divide-gray-50">
            {comparativo.map((item, i) => (
              <div key={i} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-3">
                  <Check className="h-4 w-4 text-teal-500 shrink-0" />
                  <span className="text-sm text-gray-700">{item.item}</span>
                </div>
                <span className="text-sm text-gray-400 font-medium whitespace-nowrap ml-4">{item.valorMercado}</span>
              </div>
            ))}
          </div>
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <span className="font-bold text-gray-900">Valor total no mercado</span>
            <span className="font-bold text-gray-400 line-through">{totalMercado}</span>
          </div>
          <div className="bg-teal-50 px-6 py-5 flex items-center justify-between">
            <span className="font-bold text-teal-900 text-lg">Seu investimento</span>
            <div className="text-right">
              <span className="font-bold text-teal-700 text-2xl">R$ 20.000</span>
              <p className="text-xs text-teal-600">R$ 10.000 de entrada + 5x R$ 2.000/semana</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Prova Social */}
      <section className="bg-gray-900 py-16">
        <div className="mx-auto max-w-4xl px-4">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-10"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            Construído por quem já entregou resultados
          </motion.h2>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            {[
              { num: '6', label: 'Livros publicados', icon: BookOpen },
              { num: '140+', label: 'Aulas produzidas', icon: GraduationCap },
              { num: '3', label: 'Plataformas online', icon: Monitor },
              { num: '60+', label: 'Artigos de blog', icon: FileText },
            ].map((stat, i) => {
              const Icon = stat.icon
              return (
                <div key={i} className="bg-gray-800 rounded-xl p-5 text-center border border-gray-700">
                  <Icon className="h-6 w-6 text-teal-400 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-white">{stat.num}</p>
                  <p className="text-sm text-gray-400 mt-1">{stat.label}</p>
                </div>
              )
            })}
          </motion.div>

          <motion.div
            className="mt-8 bg-gray-800 rounded-2xl p-6 md:p-8 border border-gray-700"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
          >
            <div className="flex items-start gap-4">
              <div className="bg-teal-500/20 rounded-full p-3 shrink-0">
                <Star className="h-6 w-6 text-teal-400" />
              </div>
              <div>
                <p className="text-gray-300 leading-relaxed mb-3">
                  A plataforma que você vai receber é baseada no mesmo sistema que roda o Negócios S/A
                  (app.antoniofogaca.com.br), com área de membros, 31 aulas, blog, integrações de pagamento
                  e gestão de usuários. Não é protótipo. É sistema de produção rodando em clientes reais.
                </p>
                <p className="text-sm text-teal-400 font-medium">
                  Jonathan Machado - Arquiteto de Software, eBuz Negócios Digitais
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Garantias */}
      <section className="mx-auto max-w-4xl px-4 py-16">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Suas garantias
        </motion.h2>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          {[
            {
              icon: Shield,
              titulo: 'Aprovação por etapa',
              desc: 'Cada fase do cronograma precisa da sua aprovação antes de avançar. Você tem controle total sobre o que é entregue.',
            },
            {
              icon: Award,
              titulo: 'Código aberto',
              desc: 'A plataforma é construída com tecnologias abertas (Next.js, React, Supabase). Você nunca fica preso a um fornecedor.',
            },
            {
              icon: Heart,
              titulo: 'Suporte na implantação',
              desc: 'Treinamento completo para você operar tudo sozinho. Claude Code instalado para você ter um assistente IA no dia a dia.',
            },
          ].map((item, i) => {
            const Icon = item.icon
            return (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="bg-teal-50 rounded-xl p-3 w-fit mb-4">
                  <Icon className="h-6 w-6 text-teal-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{item.titulo}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            )
          })}
        </motion.div>
      </section>

      {/* Planos de Pagamento */}
      <section className="mx-auto max-w-3xl px-4 pb-16" id="investimento">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Condições de pagamento
        </motion.h2>
        <motion.p
          className="text-center text-gray-500 mb-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Investimento total: R$ 20.000
        </motion.p>
        <motion.div
          className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-8 max-w-md mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <p className="text-sm text-teal-800 font-medium text-center">
            R$ 10.000 de entrada agora + 5 parcelas semanais de R$ 2.000
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          {/* Card 1: Entrada PIX - R$ 10.000 */}
          {planos.map((plano) => {
            const cores = corClasses[plano.cor]
            const Icon = plano.icon
            return (
              <div
                key={plano.id}
                className={`rounded-2xl bg-white p-6 flex flex-col border-2 ${cores.border} shadow-lg relative`}
              >
                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 ${cores.badge} text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap`}>
                  {plano.badge}
                </span>

                <div className={`inline-flex items-center justify-center rounded-lg ${cores.light} p-2 w-fit mb-3`}>
                  <Icon className={`h-5 w-5 ${cores.text}`} />
                </div>

                <h3 className="font-bold text-gray-900 text-lg">{plano.nome}</h3>
                <p className="text-sm text-gray-500 mb-4">{plano.subtitulo}</p>

                <div className="mb-1">
                  <span className="text-3xl font-bold text-gray-900">R$ {plano.preco.toLocaleString('pt-BR')}</span>
                </div>
                <p className="text-xs text-gray-400 mb-6">
                  PIX ou boleto bancário
                </p>

                <div className="flex-1" />

                <ul className="space-y-2 mb-6">
                  {[
                    'Todos os entregáveis inclusos',
                    'Cronograma de 12 semanas',
                    'Aprovação por etapa',
                    'Código aberto (sem lock-in)',
                  ].map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className={`h-4 w-4 ${cores.text} shrink-0`} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plano)}
                  className={`w-full ${cores.btn} ${cores.btnHover} text-white py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] cursor-pointer`}
                >
                  Pagar entrada via PIX
                </button>
              </div>
            )
          })}

          {/* Card 2: Assinatura Semanal - 5x R$ 2.000 */}
          <div className="rounded-2xl bg-white p-6 flex flex-col border-2 border-blue-200 relative">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
              Restante do projeto
            </span>

            <div className="inline-flex items-center justify-center rounded-lg bg-blue-50 p-2 w-fit mb-3">
              <Calendar className="h-5 w-5 text-blue-700" />
            </div>

            <h3 className="font-bold text-gray-900 text-lg">Parcelas Semanais</h3>
            <p className="text-sm text-gray-500 mb-4">5 parcelas automáticas após a entrada</p>

            <div className="mb-1">
              <span className="text-3xl font-bold text-gray-900">5x R$ 2.000</span>
            </div>
            <p className="text-xs text-gray-400 mb-6">
              Cobrado semanalmente via Assas
            </p>

            <div className="flex-1" />

            <ul className="space-y-2 mb-6">
              {[
                'Cobrança automática semanal',
                'Link seguro Assas',
                'Recibo por email',
                'Cancele quando quiser',
              ].map((item, j) => (
                <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="h-4 w-4 text-blue-700 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <a
              href={ASSINATURA_SEMANAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] cursor-pointer text-center block"
            >
              Ativar parcelas semanais
            </a>
          </div>
        </motion.div>
      </section>

      {/* Checkout */}
      {showCheckout && selectedPlan && (
        <section id="checkout-section" className="mx-auto max-w-5xl px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Finalizar Pagamento</h2>
              <button
                onClick={() => setShowCheckout(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-start">
              {/* Card Resumo */}
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl border-2 border-teal-200 p-6 md:p-8">
                <span className="inline-block rounded-full bg-teal-100 px-3 py-1 text-xs font-semibold text-teal-700 mb-4">
                  {selectedPlan.nome}
                </span>
                <p className="text-sm text-gray-600 mb-2">Projeto Ecossistema Digital NR1</p>
                <p className="text-sm text-gray-500 mb-4">{selectedPlan.subtitulo}</p>

                <p className="text-4xl font-bold text-gray-900 mb-1">
                  R$ {selectedPlan.preco.toLocaleString('pt-BR')}
                </p>
                {selectedPlan.parcelas > 1 ? (
                  <p className="text-sm text-gray-500 mb-4">
                    {selectedPlan.parcelas}x de R$ {selectedPlan.parcelaValor} no cartão
                  </p>
                ) : (
                  <p className="text-sm text-teal-600 font-medium mb-4">
                    Entrada via PIX
                  </p>
                )}
                <div className="bg-teal-50 rounded-lg p-3 mb-6">
                  <p className="text-xs text-teal-700 font-medium">
                    Investimento total: R$ 20.000
                  </p>
                  <p className="text-xs text-teal-600 mt-0.5">
                    Entrada R$ 10.000 + 5 parcelas semanais de R$ 2.000
                  </p>
                </div>

                <ul className="space-y-2 mb-6 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-600 shrink-0" /> Plataforma digital completa</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-600 shrink-0" /> Curso com roteiros em 4 formatos</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-600 shrink-0" /> Livro + diagramação + 2 cópias</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-600 shrink-0" /> Blog com 60 artigos SEO</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-600 shrink-0" /> Podcast + funil evergreen</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-600 shrink-0" /> Claude Code + Obsidian</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-600 shrink-0" /> Mentoria + palestra</li>
                </ul>

                <div className="flex items-center gap-4 text-xs text-gray-400 pt-4 border-t border-teal-200">
                  <span className="flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5" /> Pagamento seguro
                  </span>
                  <span className="flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5" /> Contrato com escopo
                  </span>
                </div>

                <button
                  onClick={() => {
                    setShowCheckout(false)
                    setTimeout(() => {
                      document.getElementById('investimento')?.scrollIntoView({ behavior: 'smooth' })
                    }, 100)
                  }}
                  className="mt-4 text-xs text-teal-600 hover:text-teal-700 font-medium underline"
                >
                  Trocar forma de pagamento
                </button>
              </div>

              {/* Checkout Form */}
              <div>
                <RalphCheckoutForm
                  planId={selectedPlan.id}
                  planName={selectedPlan.nome}
                  planSubtitle={selectedPlan.subtitulo}
                  amount={selectedPlan.precoCentavos}
                  maxInstallments={selectedPlan.parcelas}
                  userEmail={buyerEmail}
                  userName={buyerName}
                  userPhone={buyerPhone}
                  userDocument={buyerCpf}
                />
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 pb-20">
        <motion.h2
          className="text-2xl font-bold text-gray-900 text-center mb-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Perguntas frequentes
        </motion.h2>

        <motion.div
          className="space-y-3"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          {faq.map((item, i) => (
            <div key={i} className="rounded-xl bg-white border border-gray-100 overflow-hidden">
              <button
                onClick={() => setFaqAberto(faqAberto === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="font-medium text-gray-900">{item.pergunta}</span>
                {faqAberto === i ? (
                  <ChevronUp className="h-5 w-5 text-gray-400 shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
                )}
              </button>
              {faqAberto === i && (
                <div className="px-5 pb-4">
                  <p className="text-gray-600 leading-relaxed">{item.resposta}</p>
                </div>
              )}
            </div>
          ))}
        </motion.div>
      </section>

      {/* CTA Final */}
      <section className="bg-teal-600 py-12 text-center">
        <div className="mx-auto max-w-2xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Pronto para construir seu ecossistema?
          </h2>
          <p className="text-teal-200 mb-6">
            Em 12 semanas você vai ter plataforma, curso, livro, blog, podcast e funil.
            Tudo rodando, tudo seu, tudo pronto para faturar.
          </p>
          <button
            onClick={() => {
              document.getElementById('investimento')?.scrollIntoView({ behavior: 'smooth' })
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-teal-700 font-semibold text-lg hover:bg-teal-50 active:scale-[0.98] transition-all cursor-pointer"
          >
            Ver condições de pagamento
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-gray-400">
        <p>eBuz Negócios Digitais - CNPJ 29.436.457/0001-25</p>
        <p className="mt-1">Todos os direitos reservados - 2026</p>
      </footer>
    </main>
  )
}
